import React, {useEffect, useMemo, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Box, Stack} from '@mui/material';
import {Modal} from '@lightricks/react-design-system';
import SampleAssetsModal from '@contentlab/components/feature-editor/components/sample-assets-modal/SampleAssetsModal';
import sampleAssets from '@contentlab/components/feature-editor/components/sample-assets-modal/sampleAssets';
import downloadAndUploadAsset from '@contentlab/components/feature-editor/components/sample-assets-modal/utils';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import {FeatureId} from '@/views/content-studio/constants';
import openDoAndDontModalFeatureIfNeeded from '@/views/content-studio/utils/openDoAndDontModalFeatureIfNeeded';
import contentStudioStore, {
  contentStudioActions,
} from '@/stores/contentStudioStore';
import OnboardingFeatureModal from '../onboarding-feature-modal/OnboardingFeatureModal';
import FeaturePageNavBar from './FeaturePageNavBar';

function FeaturePage({
  children,
  featureId,
}: {
  children: React.ReactNode;
  featureId?: FeatureId;
}) {
  useEffect(() => {
    openDoAndDontModalFeatureIfNeeded(featureId);
  }, []);
  const {isOnboardingModalOpen} = contentStudioStore(
    useShallow((state) => ({
      isOnboardingModalOpen: state.isOnboardingModalOpen,
    }))
  );
  const [isShowingSampleAssets, setIsShowingSampleAssets] = useState(false);
  const [selectedSampleAsset, setSelectedSampleAsset] = useState<number | null>(
    null
  );
  const {viewPresented} = useViewPresentedEvent();
  const {uiInteraction} = useUiInteractionEvent();

  const modalPresentationId = useMemo(() => uuid(), []);

  useEffect(() => {
    if (isOnboardingModalOpen) {
      viewPresented({
        presentation_id: modalPresentationId,
        view_name: 'onboarding_upload_modal',
        view_type: 'modal',
        view_location: '',
        source: '',
      });
    }
  }, []);

  return (
    <Stack
      id="feature-page"
      direction="column"
      height="95%"
      position="fixed"
      width="100vw"
    >
      <Stack direction="row" flex={1} width="100%" maxHeight="100%">
        <FeaturePageNavBar />
        {children}
      </Stack>
      <Modal
        open={isOnboardingModalOpen}
        showCloseButton
        handleClose={() => {
          contentStudioActions.setIsOnboardingModalOpen(false);
          uiInteraction({
            item_name: 'close_button',
            interaction_location: 'onboarding_upload_modal',
            interaction_id: uuid(),
            presentation_id: modalPresentationId,
          });
        }}
        width="608px"
        header={isShowingSampleAssets ? 'Pick a sample clip' : 'Select a video'}
        textAlignment="left"
        footerAlignment="right"
        primaryButtonText={isShowingSampleAssets ? 'Next' : ''}
        primaryButtonProps={{
          disabled: !selectedSampleAsset,
        }}
        handlePrimaryButtonClick={async () => {
          if (selectedSampleAsset) {
            await downloadAndUploadAsset(
              sampleAssets[selectedSampleAsset - 1].video,
              sampleAssets[selectedSampleAsset - 1].fileName
            );
            contentStudioActions.setIsOnboardingModalOpen(false);
            uiInteraction({
              item_name: 'sample_video_selected',
              interaction_location: 'onboarding_upload_modal',
              interaction_id: uuid(),
              presentation_id: modalPresentationId,
              asset_id: sampleAssets[selectedSampleAsset - 1].fileName,
            });
          }
        }}
        secondaryButtonText={isShowingSampleAssets ? 'Back' : ''}
        handleSecondaryButtonClick={() => {
          setIsShowingSampleAssets(false);
        }}
      >
        <>
          {isShowingSampleAssets ? (
            <SampleAssetsModal
              selectedAsset={selectedSampleAsset}
              setSelectedAsset={(assetId) => {
                setSelectedSampleAsset(assetId);
                uiInteraction({
                  item_name: 'sample_video_changed',
                  interaction_location: 'onboarding_upload_modal',
                  interaction_id: uuid(),
                  presentation_id: modalPresentationId,
                  ...(assetId && {
                    asset_id: sampleAssets[assetId - 1].fileName,
                  }),
                });
              }}
            />
          ) : (
            <OnboardingFeatureModal
              onClickSampleAssets={() => {
                setIsShowingSampleAssets(true);
                uiInteraction({
                  item_name: 'explore_with_sample_video',
                  interaction_location: 'onboarding_upload_modal',
                  interaction_id: uuid(),
                  presentation_id: modalPresentationId,
                });
              }}
            />
          )}
          {isShowingSampleAssets && (
            // We use this div as a divider between the sample assets and the buttons.
            // We can't use a divider component because it only extends to the width of the modal content container.
            <Box
              position="relative"
              height="1px"
              width="604px"
              bgcolor="#E6E6E6"
            />
          )}
        </>
      </Modal>
    </Stack>
  );
}

export default FeaturePage;
